/**
 * Painel admin de Performance do Chat
 * Compara o uso de queries diretas em messages/message_attachments
 * com as RPCs SECURITY DEFINER após a migração.
 */
import { useMemo, useState } from 'react';
import {
  Activity,
  RefreshCw,
  Camera,
  Target,
  Trash2,
  ArrowDown,
  ArrowUp,
  MessageSquare,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useChatPerfMetrics,
  CHAT_PERF_KEYS,
  type ChatPerfKey,
  type ChatPerfMetricRow,
} from '@/hooks/useChatPerfMetrics';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Rótulos amigáveis por chave
const KEY_LABELS: Record<ChatPerfKey, string> = {
  msgs_select_direct: 'SELECT direto · messages',
  msg_attachments_select_direct: 'SELECT direto · message_attachments',
  rpc_get_conversation_messages_page: 'RPC · get_conversation_messages_page',
  rpc_get_message_full_by_id: 'RPC · get_message_full_by_id',
  rpc_resolve_reply_to_id_by_external: 'RPC · resolve_reply_to_id_by_external',
  rpc_count_conversation_messages: 'RPC · count_conversation_messages',
};

const LEGACY_KEYS: ChatPerfKey[] = ['msgs_select_direct', 'msg_attachments_select_direct'];

function findRow(rows: ChatPerfMetricRow[] | undefined, key: ChatPerfKey) {
  return rows?.find((r) => r.query_key === key);
}

function fmt(n?: number, digits = 0) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('pt-BR', { maximumFractionDigits: digits });
}

export default function ChatPerformance() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [snapshotLabel, setSnapshotLabel] = useState('manual');
  const [evolutionMetric, setEvolutionMetric] = useState<'calls' | 'mean_ms' | 'total_ms'>('calls');

  const {
    data,
    isLoading,
    refetch,
    isFetching,
    captureSnapshot,
    resetBaseline,
    deleteSnapshot,
  } = useChatPerfMetrics(autoRefresh);

  const current = data?.current ?? [];
  const baseline = data?.baseline ?? null;
  const series = data?.series ?? [];
  const tables = data?.tables ?? [];

  // Dados do BarChart comparativo
  const compareData = useMemo(() => {
    return CHAT_PERF_KEYS.map((k) => {
      const cur = findRow(current, k);
      const base = findRow(baseline?.metrics, k);
      return {
        name: KEY_LABELS[k].replace('RPC · ', '').replace('SELECT direto · ', ''),
        key: k,
        baseline: base?.calls ?? 0,
        current: cur?.calls ?? 0,
        baseline_mean: Number(base?.mean_ms ?? 0).toFixed(2),
        current_mean: Number(cur?.mean_ms ?? 0).toFixed(2),
      };
    });
  }, [current, baseline]);

  // Dados de evolução temporal
  const evolutionData = useMemo(() => {
    return series.map((s) => {
      const point: any = {
        ts: format(new Date(s.captured_at), 'dd/MM HH:mm', { locale: ptBR }),
      };
      CHAT_PERF_KEYS.forEach((k) => {
        const r = s.metrics?.find((m) => m.query_key === k);
        const val =
          evolutionMetric === 'calls'
            ? r?.calls
            : evolutionMetric === 'mean_ms'
              ? r?.mean_ms
              : r?.total_ms;
        point[k] = val ?? 0;
      });
      return point;
    });
  }, [series, evolutionMetric]);

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            Performance do Chat
          </h1>
          <p className="text-sm text-muted-foreground">
            Comparativo antes × depois da migração das queries de mensagens para RPCs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-card">
            <Switch
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
            />
            <Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
              Auto 30s
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <div className="flex items-center gap-1">
            <Input
              value={snapshotLabel}
              onChange={(e) => setSnapshotLabel(e.target.value)}
              className="w-32 h-9"
              placeholder="label"
            />
            <Button
              size="sm"
              onClick={() => captureSnapshot.mutate(snapshotLabel || 'manual')}
              disabled={captureSnapshot.isPending}
            >
              <Camera className="h-4 w-4 mr-2" />
              Snapshot
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => resetBaseline.mutate()}
            disabled={resetBaseline.isPending}
          >
            <Target className="h-4 w-4 mr-2" />
            Definir baseline
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CHAT_PERF_KEYS.map((k) => {
          const cur = findRow(current, k);
          const base = findRow(baseline?.metrics, k);
          const isLegacy = LEGACY_KEYS.includes(k);
          const deltaCalls = (cur?.calls ?? 0) - (base?.calls ?? 0);
          const goodTrend = isLegacy ? deltaCalls <= 0 : deltaCalls >= 0;

          return (
            <Card key={k} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-sm font-medium leading-tight">
                    {KEY_LABELS[k]}
                  </CardTitle>
                  <Badge variant={isLegacy ? 'destructive' : 'default'} className="shrink-0">
                    {isLegacy ? 'Legado' : 'RPC'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">Calls</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold">{fmt(cur?.calls)}</span>
                    {base && (
                      <span
                        className={`text-xs flex items-center gap-0.5 ${
                          goodTrend ? 'text-emerald-500' : 'text-rose-500'
                        }`}
                      >
                        {deltaCalls >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                        {fmt(Math.abs(deltaCalls))}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">Mean ms</span>
                  <span className="text-sm font-semibold">{fmt(cur?.mean_ms, 2)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">Total ms</span>
                  <span className="text-sm">{fmt(cur?.total_ms, 0)}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="compare" className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="compare">Antes × Depois</TabsTrigger>
          <TabsTrigger value="evolution">Evolução</TabsTrigger>
          <TabsTrigger value="tables">Tabelas</TabsTrigger>
          <TabsTrigger value="snapshots">Snapshots</TabsTrigger>
        </TabsList>

        {/* Comparativo */}
        <TabsContent value="compare" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Chamadas: baseline vs atual</CardTitle>
              <CardDescription>
                Queries legadas devem ficar estáveis após a migração; RPCs devem crescer.
              </CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="baseline" fill="hsl(var(--muted-foreground))" name="Baseline" />
                  <Bar dataKey="current" fill="hsl(var(--primary))" name="Atual" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tempo médio (ms)</CardTitle>
              <CardDescription>Mean exec time atual por chave de query.</CardDescription>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="current_mean" fill="hsl(var(--primary))" name="Mean ms (atual)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evolução */}
        <TabsContent value="evolution" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle>Evolução temporal</CardTitle>
                <CardDescription>Histórico dos últimos snapshots.</CardDescription>
              </div>
              <div className="flex gap-1">
                {(['calls', 'mean_ms', 'total_ms'] as const).map((m) => (
                  <Button
                    key={m}
                    size="sm"
                    variant={evolutionMetric === m ? 'default' : 'outline'}
                    onClick={() => setEvolutionMetric(m)}
                  >
                    {m}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="h-96">
              {evolutionData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Nenhum snapshot capturado ainda.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={evolutionData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="ts" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {CHAT_PERF_KEYS.map((k, i) => (
                      <Line
                        key={k}
                        type="monotone"
                        dataKey={k}
                        name={KEY_LABELS[k]}
                        stroke={`hsl(${(i * 55) % 360} 70% 50%)`}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tabelas */}
        <TabsContent value="tables" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tables.map((t) => {
              const total = (t.seq_scan ?? 0) + (t.idx_scan ?? 0);
              const idxPct = total > 0 ? ((t.idx_scan / total) * 100).toFixed(1) : '0';
              return (
                <Card key={t.relname}>
                  <CardHeader>
                    <CardTitle className="text-base">{t.relname}</CardTitle>
                    <CardDescription>{idxPct}% scans usaram índice</CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">seq_scan</div>
                      <div className="font-semibold">{fmt(t.seq_scan)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">idx_scan</div>
                      <div className="font-semibold">{fmt(t.idx_scan)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">live tup</div>
                      <div className="font-semibold">{fmt(t.n_live_tup)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">dead tup</div>
                      <div className="font-semibold">{fmt(t.n_dead_tup)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">inserts</div>
                      <div className="font-semibold">{fmt(t.n_tup_ins)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">updates</div>
                      <div className="font-semibold">{fmt(t.n_tup_upd)}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Snapshots */}
        <TabsContent value="snapshots">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de snapshots</CardTitle>
              <CardDescription>{series.length} pontos armazenados.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Capturado em</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-right">Itens</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...series].reverse().map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">
                        {format(new Date(s.captured_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={s.label === 'baseline_pre_migracao' ? 'default' : 'outline'}>
                          {s.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{s.metrics?.length ?? 0}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteSnapshot.mutate(s.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {series.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        Nenhum snapshot ainda. Clique em "Definir baseline" para começar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
